import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../../common/types';
import { formatPlayerTime, hookFraction, playedFraction } from './masterPlayerFormat';
import { masterPlayerMessages } from './masterPlayerMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the master player; each part uses its own slice of it */
import styles from './MasterPlayer.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface MasterPlayerBarProps {
  positionSec: number;
  durationSec: number | null;
  hookStartSec: number | null;
  /** True while the AAC copy is not ready: a dashed bar with no times. */
  disabled?: boolean;
  /** Shown after the dashed bar of a disabled player. */
  note?: string;
  onSeek: (seconds: number) => void;
}

/**
 * The bar of the master player (design release-kit o1, o2): the elapsed time, the played part,
 * the pink hook mark and the length. A range input on top of the bar seeks with the mouse, the
 * touch and the keys. A click on the hook mark seeks to the hook.
 */
const MasterPlayerBarComponent: React.FC<MasterPlayerBarProps> = ({
  positionSec,
  durationSec,
  hookStartSec,
  disabled = false,
  note,
  onSeek,
}) => {
  const intl = useIntl();
  const onRange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onSeek(Number(event.target.value)),
    [onSeek]
  );
  const goToHook = useCallback(() => {
    if (hookStartSec !== null) {
      onSeek(hookStartSec);
    }
  }, [hookStartSec, onSeek]);

  if (disabled) {
    return (
      <div className={styles.bar}>
        <span className={styles.time}>{formatPlayerTime(null)}</span>
        <span className={clsx(styles.track, styles.trackOff)} />
        {note !== undefined && <span className={styles.note}>{note}</span>}
      </div>
    );
  }

  const hook = hookFraction(hookStartSec, durationSec);
  const played = playedFraction(positionSec, durationSec);
  const elapsed = formatPlayerTime(positionSec);
  const total = formatPlayerTime(durationSec);

  return (
    <div className={styles.bar}>
      <span className={styles.time}>{elapsed}</span>
      <span className={styles.track}>
        <span className={styles.fill} style={{ width: `${played * 100}%` }} />
        <span className={styles.knob} style={{ left: `${played * 100}%` }} />
        <input
          type="range"
          className={styles.range}
          min={0}
          max={durationSec ?? 0}
          step={1}
          value={Math.min(positionSec, durationSec ?? 0)}
          onChange={onRange}
          disabled={durationSec === null}
          aria-label={intl.formatMessage(masterPlayerMessages.seek)}
          aria-valuetext={intl.formatMessage(masterPlayerMessages.position, { elapsed, total })}
        />
        {hook !== null && (
          <button
            type="button"
            className={styles.hook}
            style={{ left: `${hook * 100}%` }}
            onClick={goToHook}
            aria-label={intl.formatMessage(masterPlayerMessages.goToHook)}
          >
            <span className={styles.hookLabel}>{intl.formatMessage(masterPlayerMessages.hook)}</span>
          </button>
        )}
      </span>
      <span className={clsx(styles.time, styles.timeEnd)}>{total}</span>
    </div>
  );
};

export const MasterPlayerBar = TypedMemo(MasterPlayerBarComponent);
